<script lang="ts" setup>
import { ref } from 'vue';
import { useAppStore } from '../stores/app';
import { getMenus } from '../utils/menu';
import useRegistrationUrl from '../hooks/registration';

defineOptions({
  name: 'PageMenu',
});

const { config } = useAppStore();
const registrationUrl = useRegistrationUrl();

const hamburger = defineModel<boolean>('hamburger');
const flEl = ref<HTMLElement>();
let isAnimating = false;
const menus = getMenus(config);

const slideUp = (element: HTMLElement) => {
  if (isAnimating) {
    return;
  }

  isAnimating = true;

  element.style.height = element.scrollHeight + 'px';
  element.style.height = '0px';

  setTimeout(() => {
    element.style.display = 'none';
    isAnimating = false;
  }, 300);
}

const slideDown = (element: HTMLElement) => {
  if (isAnimating) {
    return;
  }

  isAnimating = true;

  element.style.display = 'block';

  const height = element.scrollHeight + 'px';

  element.style.height = '0px';
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  element.offsetHeight;

  setTimeout(() => {
    element.style.height = height;
  }, 0);

  setTimeout(() => {
    isAnimating = false;
  }, 300);
}

const slideToggle = (element: HTMLElement) => {
  if (window.getComputedStyle(element).display === 'none') {
    slideDown(element);
  }
  else {
    slideUp(element);
  }
}

const handleClick = () => {
  if (isAnimating) {
    return;
  }

  const el = flEl.value;

  if (!el) {
    return;
  }

  if (window.getComputedStyle(el).display === 'none') {
    return;
  }

  const siblings = Array.from(el.parentElement?.parentElement?.children ?? []).filter(child => child !== el.parentElement);

  siblings.forEach(sibling => {
    const subMenus = sibling.querySelectorAll<HTMLElement>('.sub-menu');
    subMenus.forEach(subMenu => {
      slideUp(subMenu);
    });

    const flIcons = sibling.querySelectorAll('.fl');
    flIcons.forEach(flIcon => {
      flIcon.classList.add('flaticon-plus');
      flIcon.textContent = '+';
    });
  });

  if (el.classList.contains('flaticon-plus')) {
    el.classList.remove('flaticon-plus');
    el.classList.add('flaticon-minus');
    el.textContent = '-';
  }
  else {
    el.classList.remove('flaticon-minus');
    el.classList.add('flaticon-plus');
    el.textContent = '+';
  }

  const subMenu = el.nextElementSibling as HTMLElement;
  if (subMenu && subMenu.classList.contains('sub-menu')) {
    slideToggle(subMenu);
  }
};
</script>

<template>
  <!-- eslint-disable-next-line @unocss/order -->
  <div class="col-xl-7 col-sm-6 col-lg-8 col-md-8 col-xs-6">
    <nav class="main-nav" :class="{ 'slidenav': hamburger }">
      <!-- <div class="d-xl-none inner-logo">
        <RouterLink to="/"><img src="../assets/images/logo-v2.png" alt="logo" class="h-56 w-171 object-contain"></RouterLink>
      </div> -->
      <ul>
        <li v-for="(menu, index) in menus" :key="index">
          <template v-if="menu.isRouter">
            <RouterLink :to="menu.path" :class="{ 'active': menu.getActive?.($route) }" @click="hamburger = false">
              {{ menu.name }} <span>0{{ index + 1 }}</span>
            </RouterLink>
          </template>
          <template v-else>
            <a :href="menu.path" target="_blank">
              {{ menu.name }} <span>0{{ index + 1 }}</span>
            </a>
          </template>
        </li>

        <li class="has-child-menu">
          <a href="javascript:;" @click="handleClick">往届大会</a>
          <i class="fl flaticon-plus" ref="flEl" @click="handleClick">+</i>
          <ul class="sub-menu">
            <li><a href="https://openatom.cn/summit/2023/index/main/" target="_blank">2023 开放原子全球开源峰会</a></li>
            <li><a href="https://openatom.cn/conference/2024/index/main/" target="_blank">2024 开放原子开源生态大会</a></li>
            <li><a href="https://openatom.cn/conference/2024-2/index/main/" target="_blank">2024 开放原子开发者大会</a></li>
            <li><a href="https://openatom.cn/conference/2025-1/index/main/" target="_blank">2025 开放原子开源生态大会</a></li>
          </ul>
        </li>
      </ul>

      <div class="d-xl-none inner-btn">
        <a :href="registrationUrl" class="primary-btn-fill" target="_blank">报名</a>
      </div>
    </nav>

    <nav class="bottom-nav xl:hidden!">
      <RouterLink to="/" class="nav-item">
        <span>首页</span>
      </RouterLink>
      <RouterLink to="/#schedule" class="nav-item">
        <span>议程</span>
      </RouterLink>
      <a :href="registrationUrl" class="nav-item" target="_blank">
        <span>报名</span>
      </a>
      <RouterLink to="/profile/index" class="nav-item">
        <span>签到</span>
      </RouterLink>
    </nav>
  </div>
</template>

<style lang="scss" scoped>
.bottom-nav {
  position: fixed;
  top: auto;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1000;
  display: flex;
  align-items: stretch;
  justify-content: space-around;
  padding: 8px 0;
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
  background: linear-gradient(180deg, #fff 0%, #f8fafc 100%);
  border-top: 1px solid #e2e8f0;
  border-radius: 0;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(10px);

  &::before {
    position: absolute;
    top: 0;
    right: 0;
    bottom: auto;
    left: 0;
    height: 1px;
    background:
      linear-gradient(
        90deg,
        transparent 0%,
        #e2e8f0 20%,
        #e2e8f0 80%,
        transparent 100%
      );
    content: "";
  }

  .nav-item {
    position: relative;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: 2px;
    align-items: center;
    justify-content: center;
    height: 52px;
    margin: 0 4px;
    color: #64748b;
    font-weight: 500;
    font-size: 16px;
    text-decoration: none;
    border-radius: 12px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &::before {
      position: absolute;
      top: 50%;
      right: -6px;
      width: 2px;
      height: 32px;
      background:
        linear-gradient(
          180deg,
          transparent 0%,
          #94a3b8 20%,
          #cbd5e1 50%,
          #94a3b8 80%,
          transparent 100%
        );
      border-radius: 1px;
      transform: translateY(-50%);
      opacity: 0.8;
      content: "";
    }

    &:last-child::before {
      display: none;
    }

    span {
      position: relative;
      z-index: 1;
      transition: all 0.3s ease;
    }

    &::after {
      position: absolute;
      top: 50%;
      right: auto;
      bottom: auto;
      left: 50%;
      width: 0;
      height: 0;
      background: radial-gradient(circle, rgba(17, 128, 255, 0.1) 0%, transparent 70%);
      border-radius: 50%;
      transform: translate(-50%, -50%);
      transition: all 0.3s ease;
      content: "";
    }

    &:hover {
      color: #1e40af;
      background: rgba(17, 128, 255, 0.04);
      transform: translateY(-1px);

      &::after {
        width: 60px;
        height: 60px;
      }
    }

    &.active {
      color: #1180ff;
      background: rgba(17, 128, 255, 0.08);

      &::before {
        right: -7px;
        width: 3px;
        height: 36px;
        background:
          linear-gradient(
            180deg,
            transparent 0%,
            #1180ff 20%,
            #4dabf7 50%,
            #1180ff 80%,
            transparent 100%
          );
        opacity: 1;
      }

      span {
        font-weight: 600;
      }

      &::after {
        width: 80px;
        height: 80px;
        background: radial-gradient(circle, rgba(17, 128, 255, 0.15) 0%, transparent 70%);
      }
    }

    &:active {
      transform: translateY(0);
      transition: transform 0.1s ease;
    }
  }

  // 为不同屏幕尺寸优化
  @media (min-width: 390px) {
    .nav-item {
      margin: 0 8px;
    }
  }

  @media (max-width: 320px) {
    .nav-item {
      gap: 1px;
      margin: 0 2px;
      font-size: 11px;
    }
  }
}
</style>
